import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FaFilePdf } from 'react-icons/fa';
import { getTicketPdfs, uploadTicketPdf } from '../../services/ticketService';
import { useApp } from '../../context/AppContext';

const MAX_PDF_BYTES = 50 * 1024 * 1024;
const PDF_EXTENSIONS = ['.pdf'];

const isPdfFile = (file) => {
  if (!file) return false;
  if (file.type === 'application/pdf') return true;
  const name = String(file.name || '').toLowerCase();
  return PDF_EXTENSIONS.some((ext) => name.endsWith(ext));
};

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 1rem;
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border-primary);
  border-radius: 12px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;

  h3,
  h4 {
    margin: 0;
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    font-size: 1rem;
    font-weight: 600;
    color: var(--color-text-primary);
  }
`;

const UploadButton = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.75rem;
  border-radius: 8px;
  border: 1px solid var(--color-border-primary);
  background: var(--color-bg-tertiary);
  color: var(--color-text-primary);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};

  input {
    display: none;
  }
`;

const PdfsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const PdfItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 0.85rem;
  border: 1px solid var(--color-border-primary);
  border-radius: 10px;
  background: var(--color-bg-secondary);
`;

const PdfName = styled.span`
  font-size: 0.82rem;
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OpenLink = styled.a`
  flex-shrink: 0;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--color-text-primary);
  text-decoration: underline;
`;

const EmptyHint = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: var(--color-text-secondary);
`;

const TicketPdfSection = ({
  ticketId,
  refreshKey = 0,
  allowUpload = true,
  title = 'PDFs',
  compactTitle = false,
  onCountChange,
}) => {
  const { addToast } = useApp();
  const inputRef = useRef(null);
  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;
  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchPdfs = useCallback(async () => {
    if (!ticketId) return;
    try {
      setLoading(true);
      const response = await getTicketPdfs(ticketId);
      const list = Array.isArray(response) ? response : [];
      setPdfs(list);
      onCountChangeRef.current?.(list.length);
    } catch (err) {
      console.error('Erro ao carregar PDFs do ticket:', err);
      setPdfs([]);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    fetchPdfs();
  }, [fetchPdfs, refreshKey]);

  const handleFilesSelected = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!ticketId || files.length === 0) return;

    const pdfsToUpload = files.filter(isPdfFile);
    if (pdfsToUpload.length === 0) {
      addToast?.('Selecione um arquivo PDF.', 'error');
      return;
    }

    const oversized = pdfsToUpload.filter((file) => file.size > MAX_PDF_BYTES);
    if (oversized.length > 0) {
      addToast?.(
        'Cada PDF deve ter no máximo 50 MB. Remova os arquivos maiores e tente de novo.',
        'error'
      );
    }

    const accepted = pdfsToUpload.filter((file) => file.size <= MAX_PDF_BYTES);
    if (accepted.length === 0) return;

    try {
      setUploading(true);
      await Promise.all(accepted.map((file) => uploadTicketPdf(ticketId, file)));
      await fetchPdfs();
      addToast?.(
        accepted.length === 1 ? 'PDF enviado.' : `${accepted.length} PDFs enviados.`,
        'success'
      );
    } catch (err) {
      console.error('Erro ao enviar PDF:', err);
      addToast?.(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          (err?.response?.status
            ? `Falha ao enviar PDF (${err.response.status})`
            : 'Falha ao enviar o PDF.'),
        'error'
      );
    } finally {
      setUploading(false);
    }
  };

  if (!ticketId) return null;

  const TitleTag = compactTitle ? 'h4' : 'h3';

  return (
    <Section aria-label="PDFs do ticket">
      <SectionHeader>
        <TitleTag>
          <FaFilePdf aria-hidden />
          {title}
          {pdfs.length > 0 ? ` (${pdfs.length})` : ''}
        </TitleTag>
        {allowUpload && (
          <UploadButton $disabled={uploading}>
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,application/pdf"
              multiple
              disabled={uploading}
              onChange={handleFilesSelected}
            />
            {uploading ? 'Enviando...' : 'Anexar arquivo (PDF)'}
          </UploadButton>
        )}
      </SectionHeader>

      {loading && pdfs.length === 0 ? (
        <EmptyHint>Carregando PDFs...</EmptyHint>
      ) : pdfs.length === 0 ? (
        <EmptyHint>Nenhum PDF anexado a este ticket.</EmptyHint>
      ) : (
        <PdfsList>
          {pdfs.map((pdf, index) => (
            <PdfItem key={pdf.url || pdf.name || index}>
              <PdfName>{pdf.name || 'Arquivo PDF'}</PdfName>
              {pdf.url && (
                <OpenLink href={pdf.url} target="_blank" rel="noopener noreferrer">
                  Abrir
                </OpenLink>
              )}
            </PdfItem>
          ))}
        </PdfsList>
      )}
    </Section>
  );
};

TicketPdfSection.propTypes = {
  ticketId: PropTypes.string,
  refreshKey: PropTypes.number,
  allowUpload: PropTypes.bool,
  title: PropTypes.string,
  compactTitle: PropTypes.bool,
  onCountChange: PropTypes.func,
};

export default TicketPdfSection;
